import heroBg from "@/assets/hero-bg.jpg";
import { motion } from "framer-motion";
import { ChevronDown, Shield, CreditCard, Truck } from "lucide-react";

const floatingVariants = {
  animate: {
    y: [0, -12, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" as const },
  },
};

const Hero = () => {
  return (
    <section className="relative min-h-[110vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background with parallax-like zoom */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.15, opacity: 0 }}
        animate={{ scale: 1.02, opacity: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
      >
        <img
          src={heroBg}
          alt="Smartphones em destaque"
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Multi-layer overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/30 to-background" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/60" />

      {/* Ambient glow orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-15"
        style={{ background: "radial-gradient(circle, hsl(43 72% 45% / 0.4), transparent 70%)" }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full opacity-10"
        style={{ background: "radial-gradient(circle, hsl(43 80% 60% / 0.3), transparent 70%)" }}
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-sm mb-8"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Ofertas por tempo limitado
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h1
          className="font-display text-5xl sm:text-6xl md:text-8xl font-bold mb-6 leading-[0.95]"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
        >
          <span className="text-gradient-gold block">Os Melhores</span>
          <span className="text-foreground block mt-2">Celulares do Mercado</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
        >
          Encontre o smartphone ideal com as melhores condições de pagamento e proteja seu investimento com nosso seguro exclusivo.
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          <a
            href="#celulares"
            className="group relative inline-flex items-center justify-center px-10 py-4 rounded-xl bg-gradient-gold text-primary-foreground font-semibold text-lg overflow-hidden transition-all hover:scale-105 shadow-gold"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            <span className="relative">Ver Celulares</span>
          </a>
          <a
            href="/seguro"
            className="inline-flex items-center justify-center px-10 py-4 rounded-xl border border-primary/40 text-primary font-semibold text-lg backdrop-blur-sm hover:bg-primary/10 hover:border-primary/60 transition-all hover:scale-105"
          >
            Conheça o Seguro
          </a>
        </motion.div>

        {/* Feature pills */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 sm:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          {[
            { icon: Shield, label: "Seguro Exclusivo" },
            { icon: CreditCard, label: "Pix, Cartão & Carnê" },
            { icon: Truck, label: "Entrega Rápida" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/60 backdrop-blur-sm border border-border/50 text-sm text-muted-foreground"
            >
              <Icon className="w-4 h-4 text-primary" />
              {label}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#celulares"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-primary/60 hover:text-primary transition-colors"
        variants={floatingVariants}
        animate="animate"
        aria-label="Rolar para baixo"
      >
        <ChevronDown className="w-8 h-8" />
      </motion.a>
    </section>
  );
};

export default Hero;
